import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import {
    _api_approve_or_reject_expense_report,
    _api_externe_validation_expense_report,
} from "./api";
import { ContainerFluid } from "../../common/styles";
import HeaderDetail from "../expenses/components/Header/HeaderDetail";
import InvalidLink from "./components/InvalidLink";
import {
    ButtonEvolution,
    ContainerStyle,
    ExpensesCommentCard,
    Wrapper,
} from "../expenses/pages/ExpensesDetailReport";
import { CloseIcon, Dialog, Flex, TextArea } from "@fluentui/react-northstar";
import HeaderDetailExpenseReport from "../expenses/pages/ExpensesDetailReport/HeaderDetailExpenseReport/HeaderDetailExpenseReport";
import StatusActionsDependRole, {
    STATUS_ROLE,
} from "../expenses/components/Badges/StatusActionsDependRole";
import { AppsListDetailFilled } from "@fluentui/react-icons";
import TableRowExpense from "../expenses/components/Tables/RowExpense/TableRowExpense";
import { useMediaQuery } from "react-responsive";
import { SIZES } from "../../constants/responsive";
import EvolutionCard from "../expenses/pages/ExpensesDetailReport/EvolutionCard";
import EmbedAndCommentsCard from "../expenses/components/EmbedAndCommentsCard/EmbedAndCommentsCard";
import { useTranslation } from "react-i18next";

const ExternValidationExpenseReport = () => {
    const { hash } = useParams();
    const { t } = useTranslation();
    // states
    const [toggleComment, setToggleComment] = useState(false);
    const [comments, setComments] = useState([]); // used for toggle between comments card.
    const [embed, setEmbed] = useState(null);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [showDialogAcceptNote, setShowDialogAcceptNote] = useState(false);
    const [showDialogRefuseNote, setShowDialogRefuseNote] = useState(false);
    const [rejectComment, setRejectComment] = useState("");

    const isMobileS = useMediaQuery({
        query: `(max-width: ${SIZES.mobileM})`,
    });
    const isMobileM = useMediaQuery({
        query: `(max-width: ${SIZES.mobileL})`,
    });
    const isMobile = isMobileS || isMobileM;

    const { data, isLoading, isError, refetch } = useQuery(
        ["EXTERNE_VALIDATION_EXPENSE_REPORT", hash],
        () => {
            return _api_externe_validation_expense_report(hash);
        },
        {
            retry: false,
            refetchOnWindowFocus: false,
        }
    );

    const { mutate: mutate_approve_or_reject, isLoading: isLoadingMutate } =
        useMutation(
            ({ step, comment }) =>
                _api_approve_or_reject_expense_report({ hash, step, comment }),
            {
                onSuccess: () => {
                    setShowDialogAcceptNote(false);
                    setShowDialogRefuseNote(false);
                    setRejectComment("");
                    refetch();
                },
            }
        );

    const on_confirm_accept_note = () => {
        mutate_approve_or_reject({ step: "APPROVE", comment: "" });
    };

    const on_confirm_reject_note = () => {
        mutate_approve_or_reject({ step: "REJECT", comment: rejectComment });
    };

    const on_click_comment = (expense) => {
        const { documents, histories, id } = expense;

        if (id === selectedExpense) {
            // clicked on the same line
            setEmbed(null);
            setComments([]);
            setSelectedExpense(null);
            return;
        }

        setComments(histories);
        setSelectedExpense(id);
        setEmbed(documents.at(0)?.attached_doc);

        setToggleComment(false);
        return;
    };

    if (isLoading) {
        return null;
    }

    if (data?.success === false) {
        return <InvalidLink />;
    }

    return (
        <ContainerFluid styles={{ marginTop: "24px" }} column>
            <Dialog
                open={showDialogAcceptNote}
                cancelButton={{
                    content: t("common.cancel"),
                    flat: true,
                }}
                confirmButton={{
                    loading: isLoadingMutate,
                    content: "Accepter la note",
                    flat: true,
                }}
                content={
                    <Flex
                        fill
                        column
                        gap="gap.medium"
                        styles={{ marginTop: "15px", paddingBottom: "15px" }}
                    >
                        <p>
                            Vous êtes sur le point d'accepter cette note de
                            frais.
                        </p>
                    </Flex>
                }
                header="Acceptation de la note de frais"
                headerAction={{
                    icon: <CloseIcon />,
                    title: t("common.close"),
                    onClick: () => setShowDialogAcceptNote(false),
                }}
                onCancel={() => setShowDialogAcceptNote(false)}
                onConfirm={on_confirm_accept_note}
            />
            <Dialog
                open={showDialogRefuseNote}
                cancelButton={{
                    content: t("common.cancel"),
                    flat: true,
                }}
                confirmButton={{
                    loading: isLoadingMutate,
                    content: "Annuler la note",
                    flat: true,
                    styles: {
                        backgroundColor: "var(--color-danger)",
                        "&:hover": {
                            backgroundColor: "var(--red)",
                        },
                    },
                }}
                content={
                    <Flex
                        fill
                        column
                        gap="gap.medium"
                        styles={{ marginTop: "15px", paddingBottom: "15px" }}
                    >
                        <p>
                            Vous êtes sur le point d'annuler cette note de
                            frais.
                        </p>
                        <TextArea
                            fluid
                            rows={4}
                            placeholder="Commentaire (facultatif)"
                            value={rejectComment}
                            onChange={(_, { value }) => setRejectComment(value)}
                        />
                    </Flex>
                }
                header="Annulation de la note de frais"
                headerAction={{
                    icon: <CloseIcon />,
                    title: t("common.close"),
                    onClick: () => setShowDialogRefuseNote(false),
                }}
                onCancel={() => setShowDialogRefuseNote(false)}
                onConfirm={on_confirm_reject_note}
            />
            {/* subheader */}
            <HeaderDetail
                title={data?.name}
                reinvoiceable={data?.reinvoiceable}
                client_name={data?.client?.name}
                pre_paid={data?.pre_paid}
                objectCode={data?.object_code?.name}
                disabledSendButton={true}
                showDotsMenu={true}
                showButtonSendNote={false}
                showSaveAuto={false}
                showButtonsAcceptReject={true}
                onClickGoBack={() => null}
                showGoBackButton={false}
                onClickAcceptNote={() => setShowDialogAcceptNote(true)}
                onClickRejectNote={() => setShowDialogRefuseNote(true)}
            />
            <ContainerStyle>
                <Wrapper
                    fill
                    column
                    gap="gap.small"
                    styles={{
                        height: "fit-content",
                    }}
                >
                    <Flex vAlign="center" space="between">
                        {/* header */}
                        <HeaderDetailExpenseReport user={data?.user} />
                        <StatusActionsDependRole
                            role={STATUS_ROLE.employee}
                            status={data.status?.value}
                        />
                        <Flex column hAlign="end">
                            <ButtonEvolution
                                content={data?.history?.length}
                                icon={<AppsListDetailFilled />}
                                onClick={() => {
                                    setToggleComment((state) => !state);
                                    setComments([]);
                                    setEmbed(null);
                                    return;
                                }}
                                disabled={data?.history?.length === 0}
                                iconPosition={"after"}
                                active={toggleComment}
                                text
                                primary
                                iconOnly
                            />
                        </Flex>
                    </Flex>
                    {/* white section */}
                    <TableRowExpense
                        IS_CAN_SEND={true}
                        detail={{ expense_report: data }}
                        id_report={data?.id}
                        REPORT_STATUS={data?.status?.value}
                        isMobile={isMobile}
                        selectedExpense={selectedExpense}
                        LENGTH_EXPENSES={data?.expenses?.length}
                        handle_click_comment={on_click_comment}
                        disableMoreItem={true}
                        onClickTitle={() => null}
                    />
                </Wrapper>
                {/* right section */}
                <Flex>
                    {toggleComment && (
                        <Flex
                            fill
                            styles={{
                                marginTop: "8px",
                            }}
                        >
                            <EvolutionCard
                                history={data?.history}
                                amount_approved={data?.amount_approved}
                                currency={data?.currency_symbol}
                                setToggleComment={setToggleComment}
                            />
                        </Flex>
                    )}
                </Flex>
                <ExpensesCommentCard fill>
                    {(comments.length > 0 || embed !== null) && (
                        <EmbedAndCommentsCard
                            comments={comments}
                            embed={embed}
                            onClose={() => {
                                setEmbed(null);
                                setComments([]);
                                setSelectedExpense(null);
                            }}
                        />
                    )}
                </ExpensesCommentCard>
            </ContainerStyle>
        </ContainerFluid>
    );
};

export default ExternValidationExpenseReport;
